import { useCallback, useEffect, useState } from 'react'
import type { JSX } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import { useQuery } from '@apollo/client/react'
import { MediaType, UploadSource } from '@buffer-mono/uploader'

import type { Idea } from '~publish/pages/Create/types'
import useIdeasUploader from '~publish/hooks/useIdeasUploader'

import { IdeaComposer as LegacyIdeaComposer } from '../LegacyIdeaComposer'
import { useIdeaComposerVariant } from './IdeaComposerPanelContext'
import { GetIdea } from './IdeaDetailsDialog'
import type { IdeaCardIdea } from '~publish/pages/Create/components/IdeaCard/fragments/IdeaCard'

function toDraftIdea(idea: IdeaCardIdea): Idea {
  const media = idea.content.media?.map(
    ({ id, url, alt, thumbnailUrl, type, size, source }) => {
      const mediaType =
        Object.values(MediaType).find((candidate) => candidate === type) ??
        MediaType.unsupported

      return {
        id,
        url,
        alt: alt ?? undefined,
        thumbnailUrl: thumbnailUrl ?? undefined,
        type: mediaType,
        size: size ?? undefined,
        source:
          UploadSource.new(source) ??
          UploadSource.new(source ? { ...source, author: '' } : undefined),
      }
    },
  )

  return {
    id: idea.id,
    content: {
      title: idea.content.title ?? undefined,
      text: idea.content.text ?? undefined,
      aiAssisted: idea.content.aiAssisted,
      date: idea.content.date ?? undefined,
      services: idea.content.services,
      media,
      tags: idea.content.tags.map(({ id, name, color }) => ({
        id,
        name,
        color,
      })),
    },
    groupId: idea.groupId ?? undefined,
    position: idea.position ?? undefined,
    createdAt: idea.createdAt,
  }
}

/**
 * Component used to open IdeaComposer with existing idea.
 * Renders when corresponding route is matched.
 *
 * It will attempt to fetch the idea with the provided ideaId, returning it from cache first if available.
 */
export function IdeaEditDialog(): JSX.Element | null {
  const [isOpen, setIsOpen] = useState(true)
  const navigate = useNavigate()
  const location = useLocation()
  const variant = useIdeaComposerVariant()

  const { ideaId } = useParams<{ ideaId: string; id: string }>()
  const { data, previousData, loading } = useQuery(GetIdea, {
    variables: {
      input: {
        id: ideaId ?? '',
      },
    },
    fetchPolicy: 'cache-first',
  })
  const uploader = useIdeasUploader()

  /**
   * When the composer is closed, we want to navigate back to the original page
   * that opened the composer to ensure a seamless experience.
   * The '..' path will navigate up one level in the route tree.
   * (This is based on the component routing levels, not literal path segments)
   *
   * Keeping the `search` (query params) is a bit of a hack, but it prevents
   * re-rendering the /create page, since without params the page will auto-add
   * the ?view=board and thus cause a flash/re-render + resetting scroll,
   * which is not what we want.
   */
  const navigateBack = useCallback((): void => {
    navigate({
      pathname: '..',
      search: location.search,
    })
  }, [navigate, location.search])

  const handleOpenChange = (open: boolean): void => {
    setIsOpen(open)
    // The panel variant navigates from onExited instead, after its exit
    // animation finishes; navigating here would unmount it instantly.
    if (!open && variant === 'dialog') {
      navigateBack()
    }
  }

  // While switching from one idea to another, keep rendering the previous
  // idea so the composer stays mounted; unmounting here would close and
  // reopen the panel around every load.
  const resolvedData = data ?? previousData

  const isMissingIdea =
    !loading &&
    (resolvedData?.idea?.['__typename'] !== 'Idea' || !resolvedData?.idea)

  // Redirect from an effect: navigation is a side effect and the render
  // phase must stay pure. Nothing animates out here because the composer
  // never rendered.
  useEffect(() => {
    if (isMissingIdea) {
      navigateBack()
    }
  }, [isMissingIdea, navigateBack])

  if (isMissingIdea) {
    return null
  }

  if (resolvedData?.idea?.['__typename'] !== 'Idea') {
    return <></>
  }

  const idea = resolvedData.idea

  return (
    <LegacyIdeaComposer
      // Remount per idea: composer state and the editor initialize once on
      // mount, so without the key a switched-to idea would show stale content.
      key={idea.id}
      initialIdea={toDraftIdea(idea)}
      open={isOpen}
      onOpenChange={handleOpenChange}
      uploader={uploader}
      variant={variant}
      onExited={navigateBack}
    />
  )
}
